import { useRef, useEffect, useState, useCallback, useContext } from 'react';
import isFunction from 'lodash/isFunction';
import isUndefined from 'lodash/isUndefined';
import isEqual from 'react-fast-compare';
import warning from 'warning';
import useLayoutEffect from './useIsomorphicLayoutEffect';
import FormFieldControl from '../controllers/FormFieldControl';
import useIsEqualMemoize from './useIsEqualMemoize';
import createRef from '../utils/createRef';
import { MultistepStepContext, FormGroupContext, FormContext } from '../Context';

export default function useFormField({
  $ref: userRef,
  defaultValue,
  formFieldApiRef,
  formFieldStateRef: userFormFieldStateRef,
  initialValue,
  keepState,
  name,
  onAfterValidate,
  onBeforeValidate,
  onBlur,
  onChange,
  onFocus,
  onInvalid,
  onValid,
  property,
  relevant: userRelevant,
  schema,
  validate,
  ...userProps
}) {
  const relevantRef = useRef();
  const stepRelevantRef = useRef();
  const { isMultistepField, relevant: stepRelevant } = useContext(MultistepStepContext);
  const parentRef = useContext(FormGroupContext);
  const context = useContext(FormContext);
  const isMounted = useRef(false);
  const isUnmounted = useRef(false);

  const [formFieldControl] = useState(() =>
    new FormFieldControl({
      ...userProps,
      defaultValue,
      initialValue,
      isMultistepField,
      keepState,
      relevant: userRelevant,
      schema,
    }).withContext(
      {
        ...context,
        ...context.stateRef.current,
      },
      createRef(userRef ?? property ?? name, parentRef)
    )
  );

  const isEqualSchema = useIsEqualMemoize(schema);
  const isEqualDefaultValue = useIsEqualMemoize(defaultValue);
  const isEqualInitialValue = useIsEqualMemoize(initialValue);

  formFieldControl.setProps({
    ...userProps,
    defaultValue,
    initialValue,
    isMultistepField,
    keepState,
    relevant: userRelevant,
    schema,
  });

  const whenMounted = (fn) => {
    if (!isMounted.current) {
      return () => {};
    }

    return fn;
  };

  useEffect(
    whenMounted(() => {
      formFieldControl.updateUserSchema(schema);
    }),
    [isEqualSchema]
  );

  useEffect(
    whenMounted(() => {
      formFieldControl.updateDefaultValue(defaultValue);
    }),
    [isEqualDefaultValue]
  );

  useEffect(
    whenMounted(() => {
      formFieldControl.updateInitialValue(initialValue);
    }),
    [isEqualInitialValue]
  );

  const {
    $ref,
    actions,
    dataPath,
    defaultHandler,
    field,
    getErrorMessage,
    getProps,
    getUserProps,
    max,
    maxLength,
    min,
    minLength,
    pattern,
    properties,
    registerFieldRef,
    relevant,
    required,
    schemaValidate,
    subscribe,
    subscribeContext,
    type,
    unsubscribe,
    unsubscribeContext,
  } = formFieldControl;

  relevantRef.current = relevant;
  stepRelevantRef.current = stepRelevant;

  const [formFieldApi] = useState(actions);
  const [formFieldState, setFormFieldState] = useState(properties);
  const formFieldStateRef = useRef();

  formFieldStateRef.current = formFieldState;

  const formFieldRef = useRef();

  const relevanceCallback = useCallback((formState) => {
    const isStepRelevant = isFunction(stepRelevantRef.current)
      ? stepRelevantRef.current(formState)
      : true;
    let isFieldRelevant = true;

    if (isFunction(relevantRef.current)) {
      isFieldRelevant = relevantRef.current(formState);
    }

    return isStepRelevant && isFieldRelevant;
  }, []);

  const updateHandler = () => {
    const { properties: nextFormFieldState } = formFieldControl.withContext(
      {
        ...context,
        ...context.stateRef.current,
      },
      formFieldControl.$ref
    );

    if (!isEqual(formFieldStateRef.current, nextFormFieldState)) {
      setFormFieldState(nextFormFieldState);
    }
  };

  const [isRelevant, setIsRelevant] = useState(() => {
    const initialIsRelevant = relevanceCallback(context.stateRef.current);

    context.setFieldRelevance(dataPath, initialIsRelevant);

    return initialIsRelevant;
  });

  useEffect(
    whenMounted(() => {
      const hasField = context.getField(dataPath) != null;

      if (isRelevant && !hasField) {
        formFieldControl.register();
      } else if (!isRelevant && hasField) {
        formFieldControl.unregister();
      }
    }),
    [isRelevant, dataPath]
  );

  // unsubscribe when unmounting! this is important!!
  useLayoutEffect(() => {
    if (isRelevant) {
      formFieldControl.register();
      updateHandler();
    }

    const stateSubscription = () => {
      // skip if unmounting happened around the same time as unsubscribing
      // TODO: Investigate why this is happening
      if (!isUnmounted.current) {
        setIsRelevant(formFieldControl.isRelevant());

        updateHandler();
      }
    };

    subscribeContext();
    registerFieldRef(formFieldRef);

    context.subscribe('state', stateSubscription);

    formFieldControl.setRelevanceCallback(relevanceCallback);

    isMounted.current = true;

    return () => {
      unsubscribeContext();

      formFieldControl.unregister();

      context.unsubscribe('state', stateSubscription);

      isMounted.current = false;
      isUnmounted.current = true;
    };
  }, []);

  useEffect(whenMounted(updateHandler), [userRef, name, property]);

  useEffect(() => formFieldControl.trigger('update', formFieldApi, formFieldState), [
    formFieldState,
  ]);

  const validateMax = useCallback(
    (value) => {
      if (!isUndefined(max) && value > max) {
        return getErrorMessage('max', `should NOT be more than ${max}`);
      }

      return undefined;
    },
    [max]
  );

  const validateMaxLength = useCallback(
    (value) => {
      if (!isUndefined(maxLength) && value?.length > maxLength) {
        return getErrorMessage('maxLength', `should NOT be more than ${maxLength} characters`);
      }

      return undefined;
    },
    [maxLength]
  );

  const validateMin = useCallback(
    (value) => {
      if (!isUndefined(min) && value < min) {
        return getErrorMessage('min', `should NOT be less than ${min}`);
      }

      return undefined;
    },
    [min]
  );

  const validateMinLength = useCallback(
    (value) => {
      if (!isUndefined(minLength) && value?.length < minLength) {
        return getErrorMessage('minLength', `should NOT be shorter than ${minLength} characters`);
      }

      return undefined;
    },
    [minLength]
  );

  const validatePattern = useCallback(
    (value) => {
      if (!isUndefined(pattern) && !new RegExp(pattern).test(value)) {
        return getErrorMessage('pattern', `should match pattern "${pattern}"`);
      }

      return undefined;
    },
    [pattern]
  );

  const validateRequired = useCallback(
    (value) => {
      const valueIsEmpty = value == null || value === '';

      if (required && valueIsEmpty) {
        return typeof required === 'string'
          ? required
          : getErrorMessage('required', 'is a required property');
      }

      return undefined;
    },
    [required]
  );

  const handleSubmit = useCallback(() => formFieldControl.setTouched(true), []);

  // eslint-disable-next-line consistent-return
  useLayoutEffect(() => {
    if (isRelevant) {
      // required must come first
      subscribe('validate', validateRequired);
      subscribe('validate', validateMin);
      subscribe('validate', validateMinLength);
      subscribe('validate', validateMax);
      subscribe('validate', validateMaxLength);
      subscribe('validate', validatePattern);
      subscribe('submit', handleSubmit);

      if (onAfterValidate) subscribe('aftervalidate', onAfterValidate);
      if (onBeforeValidate) subscribe('beforevalidate', onBeforeValidate);
      if (onBlur) subscribe('blur', onBlur);
      if (onChange) subscribe('change', onChange);
      if (onFocus) subscribe('focus', onFocus);
      if (onInvalid) subscribe('invalid', onInvalid);
      if (onValid) subscribe('valid', onValid);
      if (validate) subscribe('validate', validate);
      if (schemaValidate) subscribe('validate', schemaValidate);

      return () => {
        unsubscribe('validate', validateRequired);
        unsubscribe('submit', handleSubmit);

        if (onAfterValidate) unsubscribe('aftervalidate', onAfterValidate);
        if (onBeforeValidate) unsubscribe('beforevalidate', onBeforeValidate);
        if (onBlur) unsubscribe('blur', onBlur);
        if (onChange) unsubscribe('change', onChange);
        if (onFocus) unsubscribe('focus', onFocus);
        if (onInvalid) unsubscribe('invalid', onInvalid);
        if (onValid) unsubscribe('valid', onValid);
        if (validate) unsubscribe('validate', validate);
        if (schemaValidate) unsubscribe('validate', schemaValidate);
      };
    }
  }, [
    isRelevant,
    onAfterValidate,
    onBeforeValidate,
    onBlur,
    onChange,
    onFocus,
    onInvalid,
    onValid,
    schemaValidate,
    validate,
    validateMax,
    validateMaxLength,
    validateMin,
    validateMinLength,
    validatePattern,
    validateRequired,
  ]);

  useEffect(() => {
    warning(
      type !== 'object',
      `$ref \`${$ref}\` should not be an object, did you mean to use <FormGroup> instead?`
    );
  }, [field, type]);

  const render = (children) => {
    if (!isRelevant) {
      return null;
    }

    return children;
  };

  const formFieldContext = {
    ...formFieldApi,
    ...formFieldState,
  };

  /* eslint-disable no-param-reassign */
  if (formFieldApiRef) {
    formFieldApiRef.current = formFieldApi;
  }

  if (userFormFieldStateRef) {
    userFormFieldStateRef.current = formFieldState;
  }

  return {
    defaultHandler,
    formFieldApi,
    formFieldContext,
    formFieldProps: getProps(),
    formFieldRef,
    formFieldState,
    isRelevant,
    render,
    userProps: getUserProps(),
  };
}
